import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { Box, Chip, Divider, IconButton, Tooltip, Typography } from "@mui/material";

import { Route } from "~services/router/Route";

import { CategoryBadge } from "../../atoms/CategoryBadge/CategoryBadge";
import { TaskTypeBadge } from "../../atoms/TaskTypeBadge/TaskTypeBadge";
import { type CarouselTaskItem } from "../../templates/TaskDashboardTemplate/taskUtils";
import { TagList } from "../TagList/TagList";

interface TaskContentProps {
  task: CarouselTaskItem;
}

export const TaskContent = ({ task }: TaskContentProps) => {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        justifyContent: "space-between",
      }}
    >
      {/* Header section */}
      <Box>
        <Box sx={{ alignItems: "flex-start", display: "flex", gap: 0.5, mb: 1 }}>
          <Typography
            variant="h6"
            title={task.title || undefined}
            sx={{ flex: 1, lineHeight: 1.3, minWidth: 0, overflowWrap: "break-word" }}
          >
            {task.title}
          </Typography>
          {task.type === "sales" && task.prospectId && (
            <Tooltip title="Open prospect in new tab">
              <IconButton
                aria-label="Open prospect in new tab"
                onClick={(e) => {
                  e.stopPropagation();
                  window.open(Route.salesPortalProspectDetails({ prospectId: task.prospectId ?? "" }), "_blank");
                }}
                size="small"
                sx={{ flexShrink: 0 }}
              >
                <OpenInNewIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>

        <Box sx={{ alignItems: "center", display: "flex", flexWrap: "wrap", gap: 1, mb: 1.5 }}>
          <TaskTypeBadge type={task.type} />

          {task.category && <CategoryBadge category={task.category} />}

          {/* Additional chips (e.g., Region, Time Zone for sales tasks) */}
          {task.chips?.map((chip, index) => (
            <Chip key={`chip-${index}`} label={chip} size="small" variant="outlined" sx={{ fontSize: "0.75rem" }} />
          ))}
        </Box>

        {/* Entity information */}
        {task.entityName && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1, minWidth: 0, overflowWrap: "anywhere" }}>
            {task.entityType}: <strong>{task.entityName}</strong>
          </Typography>
        )}

        <Divider sx={{ my: 1 }} />
      </Box>

      {/* Body section */}
      <Box sx={{ flex: 1, overflow: "hidden" }}>
        {/* Task description - limit to 2 lines */}
        <Typography
          variant="body2"
          sx={{
            WebkitBoxOrient: "vertical",
            WebkitLineClamp: 2,
            display: "-webkit-box",
            lineHeight: "1.5em",
            maxHeight: "3em",
            mb: 1.5,
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {task.description || "No description provided"}
        </Typography>

        {/* Tags - limit to 3 */}
        {task.tags && task.tags.length > 0 && (
          <Box sx={{ maxHeight: "28px", mt: 1.5, overflow: "hidden" }}>
            <TagList tags={task.tags.slice(0, 3)} />
          </Box>
        )}
      </Box>

      {/* Footer section */}
      <Box sx={{ mt: "auto", pt: 1 }}>
        <Divider sx={{ mb: 1 }} />
        {/* Due date - positioned on the left to leave space for the Complete button on the right */}
        {task.dueAt && (
          <Box
            sx={{
              // Match the approximate height of the button
              alignItems: "center",

              display: "flex",

              height: "32px",

              justifyContent: "flex-start",
              // Reserve space for Complete / Completing… (absolute in TaskCard)
              pr: 20,
            }}
          >
            <Typography variant="body2" color="text.secondary">
              Due: {new Date(task.dueAt).toLocaleDateString()}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};
